const prefix = import.meta.env.VITE_API_URL;
import { useStore } from "./store";
import { navigate } from "wouter/use-browser-location";
import { notifications } from "@mantine/notifications";

const jsonHeaders = {
  "content-type": "application/json",
  accept: "application/json",
};

function getAuthHeaders() {
  const authToken = useStore.getState().authToken;
  if (authToken) return { authorization: `Bearer ${authToken}` };
  return {};
}

async function throwOnError(response) {
  if (response.ok) return;
  if (response.status === 502) {
    notifications.show({
      color: "red",
      autoClose: 4000,
      title: "System Unavailable",
      message:
        "Seems our servers are having a bad day. Sorry about that! Please try again in a few minutes.",
    });
    throw new Error("Gateway Timeout");
  }
  if (response.status === 401) {
    useStore.getState().setAuthToken(null);
    useStore.getState().setProfile(null);
    notifications.show({
      color: "red",
      autoClose: 4000,
      title: "You are logged out",
      message: "Please login again",
    });
    navigate("/", { replace: true });
  }

  if (response.status === 429)
    throw new Error(
      "Hold your horses! You've reached the rate limit for now. Give our GPUs some time to cool off and check back later!"
    );

  let json = {};
  let text = "";
  try {
    text = await response.text();
    json = await response.json();
  } catch (e) {}

  const message = `An error has occured: ${response.status}: ${response.statusText}}`;
  throw new Error(json?.detail ?? json?.title ?? text ?? message);
}

export async function jsonApiRequest(method, uri, values) {
  let authHeader = getAuthHeaders();
  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
  };

  const response = await fetch(reqDetails.url, {
    method: reqDetails.method,
    headers: reqDetails.headers,
    body: reqDetails.body,
  });

  await throwOnError(response);
  if (response.status === 204) return {};

  const json = await response.json();
  return json;
}

export async function jsonPost(uri, values) {
  return await jsonApiRequest("POST", uri, values);
}

export async function jsonGet(uri) {
  return await jsonApiRequest("GET", uri, null);
}

export async function jsonPut(uri, values) {
  return await jsonApiRequest("PUT", uri, values);
}

export async function jsonDelete(uri) {
  return await jsonApiRequest("DELETE", uri, null);
}

export async function pollJob(jobId) {
  if (!jobId) throw "missing required parameter jobId!";
  return await jsonGet(`jobs/${jobId}`);
}

export async function getJobResults(jobId) {
  if (!jobId) throw "missing required parameter jobId!";
  return await jsonGet(`jobs/${jobId}/results`);
}

export async function deleteGalleryImage(imageId) {
  return await jsonDelete(`images/${imageId}`);
}

export async function deleteAllGalleryImages() {
  return await jsonDelete(`images/all`);
}

export async function deleteUserAccount() {
  return await jsonDelete(`auth/profile`);
}

export async function deleteImages(imageIds) {
  // This only works for about 27 images at a time becaus of max path length
  let imgIds = imageIds
    ? "?".concat(imageIds.map((p) => `imageIds=${p}&`))
    : "";
  return await jsonDelete("images" + imgIds);
}

export async function fetchGalleryModerator(
  page,
  pageSize = 27,
  userFilter = null,
  tagsFilter = null
) {
  let tags = tagsFilter ? "".concat(tagsFilter.map((p) => `&tags=${p}`)) : "";
  let user = userFilter ? `&ownerType=${userFilter}` : "";
  return await jsonGet(
    `mod/images?page=${page}&pageSize=${pageSize}${user}${tags}`
  );
}

export async function getImage(imageId) {
  return await jsonGet(`images/${imageId}`);
}

export async function fetchGallery(
  page,
  pageSize = 27,
  styleId = null,
  userFilter = null,
  tagsFilter = null
) {
  let tags = tagsFilter ? "".concat(tagsFilter.map((p) => `&tags=${p}`)) : "";
  let user = userFilter ? `&ownerType=${userFilter}` : "";
  let style = styleId ? `&styleId=${styleId}` : "";
  // let skip = (page - 1) * pageSize;
  return await jsonGet(
    `images?page=${page}&pageSize=${pageSize}${user}${tags}${style}`
  );
}

export async function fetchGalleryPublic(
  page,
  pageSize = 27,
  styleId = null,
  userFilter = null,
  tagsFilter = null
) {
  let tags = tagsFilter ? "".concat(tagsFilter.map((p) => `&tags=${p}`)) : "";
  let user = userFilter ? `&ownerType=${userFilter}` : "";
  let style = styleId ? `&styleId=${styleId}` : "";
  let skip = (page - 1) * pageSize;
  return await jsonGet(
    `public/images?skip=${skip}&limit=${pageSize}${user}${tags}${style}`
  );
}

export async function getCustomModels(source) {
  return await jsonGet("custom-models?source=" + source);
}

export async function createTrainingSet(imageIds) {
  return await jsonPost("training-datasets", { imageIds });
}

export async function updateDataset(id, dto) {
  return await jsonPut(`training-datasets/${id}`, dto);
}

export async function startTraining(id) {
  return await jsonPost(`training-datasets/${id}/train`);
}

export async function getTrainingDataset(id) {
  return await jsonGet("training-datasets/" + id);
}

export async function deleteTrainingDataset(id) {
  return await jsonDelete(`training-datasets/${id}`);
}

export async function getTrainingDatasets() {
  return await jsonGet("training-datasets");
}

export async function createGenerateJobAdmin(dto) {
  return await jsonPost("admin/jobs/free-form", dto);
}

export const getAdminJobs = async (page, pageSize, userFilter, statusFilter) =>
  await jsonGet(
    `admin/jobs?page=${page}&pageSize=${pageSize}&userFilter=${userFilter}&statusFilter=${statusFilter}`
  );

export const getAdminJobDetails = async (id) =>
  await jsonGet(`admin/jobs/${id}`);
export const requeueJob = async (id) =>
  await jsonPost(`admin/jobs/${id}/retry`);
export const requeueJobHard = async (id) =>
  await jsonPost(`admin/jobs/${id}/retry-hard`);

// export async function getJobsAdmin() {
//   return await jsonGet("admin/jobs");
// }

// export async function getJobDetailsAdmin(jobId) {
//   return await jsonGet(`admin/jobs/${jobId}`);
// }

export async function getJobsForResultImage(imageId) {
  return await jsonGet(`mod/images/${imageId}/jobs`);
}

export async function getUsersAdmin(
  page,
  pageSize,
  search = null,
  role = null,
  plan = null
) {
  return await jsonGet(
    `admin/users?page=${page}&pageSize=${pageSize}&search=${search}&role=${role}&plan=${plan}`
  );
}

export async function getUserAdmin(id) {
  return await jsonGet(`admin/users/${id}`);
}

export async function adminSetUserPlan(dto) {
  return await jsonPut("admin/users/plan", dto);
}

export async function adminAdjustUserCredits(dto) {
  return await jsonPost("admin/users/credits", dto);
}

export async function adminImpersonateUser(dto) {
  return await jsonPost("auth/email/impersonate", dto);
}

export async function adminDeleteUser(id) {
  return await jsonDelete(`admin/users/${id}`);
}

export async function adminGetPlugins() {
  return await jsonGet("admin/plugins");
}

export async function adminGetPluginOptions(pluginId) {
  return await jsonGet(`admin/plugins/${pluginId}/options`);
}

export async function adminCreatePluginOptionValue(pluginId, optionId, value) {
  return await jsonPost(
    `admin/plugins/${pluginId}/options/${optionId}/values`,
    value
  );
}

export async function adminUpdatePluginOptionValue(
  pluginId,
  optionId,
  valueId,
  value
) {
  return await jsonPut(
    `admin/plugins/${pluginId}/options/${optionId}/values/${valueId}`,
    value
  );
}

export async function adminDeletePluginOptionValue(
  pluginId,
  optionId,
  valueId
) {
  return await jsonDelete(
    `admin/plugins/${pluginId}/options/${optionId}/values/${valueId}`
  );
}

export async function getStyles() {
  return await jsonGet("styles");
}

export async function getStyle(id) {
  return await jsonGet(`styles/${id}`);
}

export async function createStyle(style) {
  return await jsonPost(`styles`, style);
}

export async function updateStyle(style) {
  return await jsonPut(`styles/${style.id}`, style);
}

export async function getGenerationOptions(source, mediaClass = "image") {
  return await jsonGet(
    `generation/options?source=${source}&mediaClass=${mediaClass}`
  );
}

export async function deleteImage(imageId) {
  return await jsonDelete(`mod/images/${imageId}`);
}

export async function setImageApproval(logicalImageId, approval) {
  return await jsonPut(`mod/images/${logicalImageId}/approval`, { approval });
}

export async function setImageMeta(logicalImageId, dto) {
  return await jsonPut(`mod/images/${logicalImageId}/meta`, dto);
}

export async function recreateImage(imageSha, clientJobId, sessionId) {
  return await jsonPost(`jobs/images/${imageSha}/job`);
  // var response = await fetch(`${prefix}/jobs/images/${imageSha}/job`, {
  //   method: "POST",
  //   headers: {
  //     "X-Client-Job-Id": clientJobId,
  //     "X-Session-Id": sessionId,
  //   }
  // });

  // var json = await response.json();
  // return json;
}

export async function adminDeleteUserCredit(id) {
  return await jsonDelete(`admin/users/credits/${id}`);
}

export async function getProducts() {
  return await jsonGet(`payment/stripe/products`);
}
export async function checkoutSubscription(dto) {
  return await jsonPost(`payment/stripe/checkout-plan`, dto);
}
export async function checkoutCredits(dto) {
  return await jsonPost("payment/stripe/checkout-credits", dto);
}
export async function customerPortal() {
  // mixpanel.track("Customer Portal");
  return await jsonPost("payment/stripe/portal");
}
export async function createSamplingRunsAdmin(dto) {
  return await jsonPost("admin/sampling", dto);
}

export async function getSamplingRunsAdmin() {
  return await jsonGet(`admin/sampling`);
}

export async function getSamplingRunGalleryAdmin(id) {
  return await jsonGet(`admin/sampling/${id}/images`);
}

export async function getPlugins() {
  return await jsonGet("plugins");
}

export async function deletePlugin(pluginId) {
  return await jsonDelete("plugins/" + pluginId);
}

export async function uploadPlugin(file) {
  let authHeaders = getAuthHeaders();
  var response = await fetch(`${prefix}/plugins`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });
  await throwOnError(response);
  var json = await response.json();
  return json;
}

export async function uploadImage(file) {
  let authHeaders = getAuthHeaders();
  var response = await fetch(`${prefix}/images`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });

  await throwOnError(response);
  var json = await response.json();
  return json;
}

export async function createJob(operation, inputImageId) {
  return await jsonPost("jobs", { operation, inputImageId });
}

export async function generateImage(dto) {
  return await jsonPost("generation", dto);
}

export async function generateVideo(dto) {
  return await jsonPost("video-generation", dto);
}

export async function applyPluginToImage(
  pluginId,
  inputImageId,
  operation = null,
  highQuality = false
) {
  return await jsonPost(`plugins/${pluginId}/actions/apply`, {
    inputImageId,
    operation,
    highQuality,
  });
}

export const runOmniModificationModel = async (dto) =>
  await jsonPost(`generation/edit`, dto);

export async function getMyProfile() {
  return await jsonGet("auth/profile");
}

export async function getMyCredits() {
  return await jsonGet("auth/credits");
}

export async function requestLogin({ email }) {
  return await jsonPost("auth/email", { email });
}

export async function loginWithToken(token) {
  return await jsonPost("auth/email/token", { token });
}

// export async function register(provider) {
//   // var response = await fetch(`${prefix}/auth/signin?provider=${provider}`, {
//   //   method: "POST"
//   // });
//   // console.debug("Received: ", response.headers.location);
//   // console.debug("Received: ", response.url);
//   // console.debug("Received: ", response);
//   // if(response.redirected) {
//     const width = 450;
//     const height = 730;
//     const left = window.screen.width / 2 - width / 2;
//     const top = window.screen.height / 2 - height / 2;

//     const popupWindowURL = new URL("http://localhost:5173/example");
//     const code = popupWindowURL.searchParams.get('code');
//     const state = popupWindowURL.searchParams.get('state');
//     window.open(popupWindowURL, "_blank",  'menubar=no,location=no,resizable=no,scrollbars=no,status=no, width=' +
//         width +
//         ', height=' +
//         top +
//         ', left=' +
//         left);

//   // }
// }

export async function login(dto) {
  var response = await fetch(`${prefix}/login`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(dto),
  });

  if (!response.ok) {
    const message = `An error has occured: ${response.status}`;
    throw new Error(message);
  }

  var json = await response.json();
  return json;
}
